import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import Quill from 'quill';

@Component({
  selector: 'sz-editor',
  standalone: true,
  template: `
    <div class="sz-editor-wrapper border border-neutral-300 rounded-lg overflow-hidden" [style.height]="height">
      <div #editorEl class="sz-editor-content"></div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .sz-editor-wrapper { display: flex; flex-direction: column; }
    .sz-editor-content { flex: 1; overflow-y: auto; }
    :host ::ng-deep .ql-toolbar { border: none !important; border-bottom: 1px solid #e5e7eb !important; background: #f9fafb; }
    :host ::ng-deep .ql-container { border: none !important; font-family: inherit; font-size: 14px; }
    :host ::ng-deep .ql-editor { min-height: 200px; }
  `]
})
export class SzEditorComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() value: string | undefined = '';
  @Output() valueChange = new EventEmitter<string>();
  @Input() height = '100%';

  @ViewChild('editorEl') editorEl!: ElementRef<HTMLDivElement>;

  private quill!: Quill;
  private suppressEmit = false;

  ngAfterViewInit() {
    this.quill = new Quill(this.editorEl.nativeElement, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: [
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'font': [] }],
            ['bold', 'italic', 'strike', 'underline'],
            [{ 'align': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'header': [1, 2, 3, 4, 5, false] }],
            [{ 'color': [] }, { 'background': [] }],
            ['link', 'image'],
            ['clean'],
            ['blockquote', 'code-block'],
          ],
        },
      },
    });

    if (this.value) {
      this.setContent(this.value);
    }

    this.quill.on('text-change', () => {
      if (this.suppressEmit) return;
      const html = this.quill.root.innerHTML;
      this.valueChange.emit(html === '<p><br></p>' ? '' : html);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value'] && this.quill) {
      const currentHtml = this.quill.root.innerHTML;
      if (this.value !== currentHtml) {
        this.setContent(this.value || '');
      }
    }
  }

  private setContent(html: string) {
    this.suppressEmit = true;
    this.quill.clipboard.dangerouslyPasteHTML(html);
    // Re-enable emit on next tick after all Quill text-change events settle
    setTimeout(() => this.suppressEmit = false, 0);
  }

  /** Allow parent to force-update editor content bypassing ngOnChanges */
  setContentExternal(html: string) {
    if (this.quill) {
      this.setContent(html);
    }
  }

  ngOnDestroy() {
    // Quill doesn't have an explicit destroy — just let it GC
  }
}
